import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronDown, Loader2, RotateCcw } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useStructuredOutputRegressionConfig } from "@/hooks/use-structured-output-regression-config";
import type {
  StructuredOutputRegressionCaseType,
  StructuredOutputRegressionOverrides,
} from "@/types/structured-output-regression";
import { cn } from "@/utils";

interface StructuredOutputRegressionPanelProps {
  caseType: StructuredOutputRegressionCaseType;
  onCaseTypeChange: (caseType: StructuredOutputRegressionCaseType) => void;
  onOverridesChange: (overrides: StructuredOutputRegressionOverrides | null) => void;
}

function formatSchemaJson(schema: Record<string, unknown>): string {
  return JSON.stringify(schema, null, 2);
}

export function StructuredOutputRegressionPanel({
  caseType,
  onCaseTypeChange,
  onOverridesChange,
}: StructuredOutputRegressionPanelProps) {
  const { data: config, loading, error, reload } =
    useStructuredOutputRegressionConfig(caseType);

  const [promptOpen, setPromptOpen] = useState(false);
  const [schemaOpen, setSchemaOpen] = useState(true);
  const [promptDraft, setPromptDraft] = useState("");
  const [schemaDraft, setSchemaDraft] = useState("");
  const [schemaParseError, setSchemaParseError] = useState<string | null>(null);

  useEffect(() => {
    if (!config) return;
    setPromptDraft(config.prompt_template);
    setSchemaDraft(formatSchemaJson(config.schema));
    setSchemaParseError(null);
  }, [config]);

  const promptModified = useMemo(() => {
    if (!config) return false;
    return promptDraft.trim() !== config.prompt_template.trim();
  }, [config, promptDraft]);

  const schemaModified = useMemo(() => {
    if (!config || schemaParseError) return false;
    try {
      const parsed = JSON.parse(schemaDraft) as Record<string, unknown>;
      return JSON.stringify(parsed) !== JSON.stringify(config.schema);
    } catch {
      return true;
    }
  }, [config, schemaDraft, schemaParseError]);

  const emitOverrides = useCallback(
    (nextPrompt: string, nextSchema: string) => {
      if (!config) {
        onOverridesChange(null);
        return;
      }

      let parsedSchema: Record<string, unknown> | undefined;
      try {
        parsedSchema = JSON.parse(nextSchema) as Record<string, unknown>;
        setSchemaParseError(null);
      } catch (err) {
        setSchemaParseError(
          err instanceof Error ? err.message : "Invalid JSON schema",
        );
        onOverridesChange(null);
        return;
      }

      const promptChanged = nextPrompt.trim() !== config.prompt_template.trim();
      const schemaChanged =
        JSON.stringify(parsedSchema) !== JSON.stringify(config.schema);

      if (!promptChanged && !schemaChanged) {
        onOverridesChange(null);
        return;
      }

      onOverridesChange({
        case_type: caseType,
        ...(promptChanged ? { prompt_override: nextPrompt.trim() } : {}),
        ...(schemaChanged ? { schema_override: parsedSchema } : {}),
      });
    },
    [caseType, config, onOverridesChange],
  );

  useEffect(() => {
    emitOverrides(promptDraft, schemaDraft);
  }, [promptDraft, schemaDraft, emitOverrides]);

  const resetPrompt = useCallback(() => {
    if (!config) return;
    setPromptDraft(config.prompt_template);
  }, [config]);

  const resetSchema = useCallback(() => {
    if (!config) return;
    setSchemaDraft(formatSchemaJson(config.schema));
    setSchemaParseError(null);
  }, [config]);

  return (
    <section className="space-y-4 rounded-lg border bg-card p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h2 className="text-sm font-semibold">Regression configuration</h2>
          <p className="text-xs text-muted-foreground">
            Edit the prompt template or JSON schema to test changes before
            deploying. Overrides are sent with preview when modified.
          </p>
        </div>
        <Tabs
          value={caseType}
          onValueChange={(v) =>
            onCaseTypeChange(v as StructuredOutputRegressionCaseType)
          }
        >
          <TabsList>
            <TabsTrigger value="pa">PA</TabsTrigger>
            <TabsTrigger value="bv">BV</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading {caseType.toUpperCase()} defaults…
        </div>
      ) : null}

      {error ? (
        <div className="space-y-2">
          <p className="text-sm text-destructive">{error}</p>
          <Button type="button" variant="outline" size="sm" onClick={() => void reload()}>
            Retry
          </Button>
        </div>
      ) : null}

      {config ? (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span>
              Model: <code className="rounded bg-muted px-1 py-0.5">{config.model}</code>
            </span>
            {promptModified || schemaModified ? (
              <Badge variant="secondary" className="text-[10px]">
                Custom overrides active
              </Badge>
            ) : (
              <Badge variant="outline" className="text-[10px]">
                Using defaults
              </Badge>
            )}
          </div>

          <Collapsible open={promptOpen} onOpenChange={setPromptOpen}>
            <div className="rounded-md border">
              <CollapsibleTrigger asChild>
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left hover:bg-muted/40"
                >
                  <span className="flex items-center gap-2 text-sm font-medium">
                    Prompt template
                    {promptModified ? (
                      <Badge className="bg-amber-100 text-[10px] text-amber-900 hover:bg-amber-100 dark:bg-amber-950/50 dark:text-amber-100">
                        Modified
                      </Badge>
                    ) : null}
                  </span>
                  <ChevronDown
                    className={cn(
                      "h-4 w-4 shrink-0 text-muted-foreground transition-transform",
                      promptOpen && "rotate-180",
                    )}
                  />
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="space-y-2 border-t px-3 py-3">
                  <p className="text-xs text-muted-foreground">
                    Placeholders:{" "}
                    {config.prompt_placeholders.map((p) => `{${p}}`).join(", ")}
                  </p>
                  <textarea
                    value={promptDraft}
                    onChange={(e) => setPromptDraft(e.target.value)}
                    rows={16}
                    spellCheck={false}
                    className="max-h-[28rem] min-h-[12rem] w-full resize-y rounded-md border border-input bg-background px-3 py-2 font-mono text-xs leading-relaxed"
                  />
                  <div className="flex justify-end">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={resetPrompt}
                      disabled={!promptModified}
                    >
                      <RotateCcw className="mr-1 h-3.5 w-3.5" />
                      Reset prompt
                    </Button>
                  </div>
                </div>
              </CollapsibleContent>
            </div>
          </Collapsible>

          <Collapsible open={schemaOpen} onOpenChange={setSchemaOpen}>
            <div className="rounded-md border">
              <CollapsibleTrigger asChild>
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left hover:bg-muted/40"
                >
                  <span className="flex items-center gap-2 text-sm font-medium">
                    JSON schema
                    {schemaModified ? (
                      <Badge className="bg-amber-100 text-[10px] text-amber-900 hover:bg-amber-100 dark:bg-amber-950/50 dark:text-amber-100">
                        Modified
                      </Badge>
                    ) : null}
                  </span>
                  <ChevronDown
                    className={cn(
                      "h-4 w-4 shrink-0 text-muted-foreground transition-transform",
                      schemaOpen && "rotate-180",
                    )}
                  />
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="space-y-2 border-t px-3 py-3">
                  <textarea
                    value={schemaDraft}
                    onChange={(e) => setSchemaDraft(e.target.value)}
                    rows={18}
                    spellCheck={false}
                    className={cn(
                      "max-h-[32rem] min-h-[14rem] w-full resize-y rounded-md border px-3 py-2 font-mono text-xs leading-relaxed",
                      schemaParseError
                        ? "border-destructive bg-destructive/5"
                        : "border-input bg-background",
                    )}
                  />
                  {schemaParseError ? (
                    <p className="text-xs text-destructive">{schemaParseError}</p>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      Gemini response schema passed to structured output extraction.
                    </p>
                  )}
                  <div className="flex justify-end">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={resetSchema}
                      disabled={!schemaModified && !schemaParseError}
                    >
                      <RotateCcw className="mr-1 h-3.5 w-3.5" />
                      Reset schema
                    </Button>
                  </div>
                </div>
              </CollapsibleContent>
            </div>
          </Collapsible>
        </div>
      ) : null}
    </section>
  );
}
